import * as React from "react";
import {
  getTopLevelFocusableElements,
  getTopLevelMenuItems,
} from "./useCustomMenuNavigation";

/**
 * Custom hook for handling group-based Tab/Shift+Tab navigation in menus.
 *
 * This implements accessibility-compliant menu navigation where:
 * - Tab moves focus forward to the first focusable item in the next menu group/section
 * - Shift+Tab moves focus backward to the last focusable item in the previous menu group/section
 * - Arrow keys continue to work for within-group navigation
 *
 * Menu groups are identified by:
 * 1. MenuGroup elements with [role="group"] (preferred)
 * 2. Section containers that have keys like "section-0", "section-1", etc.
 * 3. Items separated by dividers with [role="separator"]
 */
export const useMenuGroupNavigation = () => {
  const findAllMenuGroups = React.useCallback(
    (parentMenu: HTMLElement): HTMLElement[] => {
      const menuGroups = Array.from(
        parentMenu.querySelectorAll<HTMLElement>('[role="group"]')
      );
      if (menuGroups.length > 0) {
        return menuGroups;
      }

      const sectionContainers = Array.from(
        parentMenu.querySelectorAll<HTMLElement>(
          '[aria-labelledby*="menu-group"]'
        )
      );
      return sectionContainers;
    },
    []
  );

  const findCurrentMenuGroupIndex = React.useCallback(
    (currentElement: HTMLElement, allMenuGroups: HTMLElement[]): number => {
      for (let i = 0; i < allMenuGroups.length; i++) {
        const group = allMenuGroups[i];
        if (group.contains(currentElement)) {
          return i;
        }
      }
      return -1;
    },
    []
  );

  const getGroupFocusableItems = React.useCallback(
    (menuGroup: HTMLElement): HTMLElement[] => {
      const focusableItems: HTMLElement[] = [];

      const menuItems = getTopLevelMenuItems(menuGroup);

      menuItems.forEach((item) => {
        if (item.hasAttribute("data-grid-columns")) {
          const validItems = getTopLevelFocusableElements(item);
          focusableItems.push(...validItems);
        } else {
          const tabIndex = item.getAttribute("tabindex");
          const isDisabled =
            item.hasAttribute("disabled") ||
            item.getAttribute("aria-disabled") === "true";

          if (!isDisabled && tabIndex !== "-1") {
            focusableItems.push(item);
          }
        }
      });

      return focusableItems;
    },
    []
  );

  const calculateTargetGroupIndex = React.useCallback(
    (
      currentIndex: number,
      totalGroups: number,
      isShiftTab: boolean
    ): number => {
      if (isShiftTab) {
        const targetIndex = currentIndex - 1;
        return targetIndex < 0 ? totalGroups - 1 : targetIndex;
      }
      const targetIndex = currentIndex + 1;
      return targetIndex >= totalGroups ? 0 : targetIndex;
    },
    []
  );

  const handleGroupNavigation = React.useCallback(
    (
      event: React.KeyboardEvent<HTMLElement>,
      currentElement: HTMLElement,
      isShiftTab: boolean
    ): boolean => {
      const parentMenu = currentElement.closest<HTMLElement>('[role="menu"]');
      if (!parentMenu) return false;

      const allMenuGroups = findAllMenuGroups(parentMenu);
      if (allMenuGroups.length <= 1) {
        return false;
      }

      const currentGroupIndex = findCurrentMenuGroupIndex(
        currentElement,
        allMenuGroups
      );
      if (currentGroupIndex === -1) {
        return false;
      }

      const targetGroupIndex = calculateTargetGroupIndex(
        currentGroupIndex,
        allMenuGroups.length,
        isShiftTab
      );
      const targetGroup = allMenuGroups[targetGroupIndex];

      const targetGroupItems = getGroupFocusableItems(targetGroup);
      if (targetGroupItems.length === 0) return false;

      const targetItem = isShiftTab
        ? targetGroupItems[targetGroupItems.length - 1]
        : targetGroupItems[0];

      if (targetItem) {
        event.preventDefault();
        event.stopPropagation();
        targetItem.focus();
        return true;
      }

      return false;
    },
    [
      findAllMenuGroups,
      findCurrentMenuGroupIndex,
      calculateTargetGroupIndex,
      getGroupFocusableItems,
    ]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>): boolean => {
      if (event.key !== "Tab") {
        return false;
      }

      const currentElement = event.target as HTMLElement;
      return handleGroupNavigation(event, currentElement, event.shiftKey);
    },
    [handleGroupNavigation]
  );

  return {
    handleKeyDown,
    handleGroupNavigation,
    findAllMenuGroups,
    getGroupFocusableItems,
  };
};
